import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import ParentDashboard from "@/pages/ParentDashboard";
import CreateAssignment from "@/pages/CreateAssignment";
import Gradebook from "@/pages/Gradebook";
import Studio from "@/pages/Studio";
import ReportCard from "@/pages/ReportCard";
import Mastery from "@/pages/Mastery";
import CurriculumOverview from "@/pages/CurriculumOverview";
import LessonLibrary from "@/pages/LessonLibrary";
import ScripturePage from "@/pages/ScripturePage";
import CalendarPage from "@/pages/CalendarPage";
import ReadingPlanPage from "@/pages/ReadingPlanPage";
import BadgesPage from "@/pages/BadgesPage";
import StudentGuide from "@/pages/StudentGuide";
import StudentDashboard from "@/pages/StudentDashboard";
import LessonPage from "@/pages/LessonPage";
import ChoresPage from "@/pages/ChoresPage";
import ScreenTimePage from "@/pages/ScreenTimePage";
import GamesPage from "@/pages/GamesPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import SpellingBankPage from "@/pages/SpellingBankPage";
import StaffManual from "@/pages/StaffManual";
import StudentManual from "@/pages/StudentManual";
import ManageAssignments from "@/pages/ManageAssignments";
import EditAssignment from "@/pages/EditAssignment";
import VideoControls from "@/pages/VideoControls";
import ContinuingEducation from "@/pages/ContinuingEducation";
import TeacherGuideSpelling from "@/pages/TeacherGuideSpelling";
import StudyGuide from "@/pages/StudyGuide";

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-[#6B7280]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "Parent" ? "/parent" : "/student"} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/parent" element={<Protected role="Parent"><ParentDashboard /></Protected>} />
            <Route path="/parent/create" element={<Protected role="Parent"><CreateAssignment /></Protected>} />
            <Route path="/parent/manage-assignments" element={<Protected role="Parent"><ManageAssignments /></Protected>} />
            <Route path="/parent/edit-assignment/:id" element={<Protected role="Parent"><EditAssignment /></Protected>} />
            <Route path="/parent/video-controls" element={<Protected role="Parent"><VideoControls /></Protected>} />
            <Route path="/parent/continuing-education" element={<Protected role="Parent"><ContinuingEducation /></Protected>} />
            <Route path="/parent/lesson/:id" element={<Protected role="Parent"><LessonPage /></Protected>} />
            <Route path="/parent/study-guide/:id" element={<Protected role="Parent"><StudyGuide /></Protected>} />
            <Route path="/parent/studio" element={<Protected role="Parent"><Studio /></Protected>} />
            <Route path="/parent/gradebook" element={<Protected role="Parent"><Gradebook /></Protected>} />
            <Route path="/parent/report-card" element={<Protected role="Parent"><ReportCard /></Protected>} />
            <Route path="/parent/mastery" element={<Protected role="Parent"><Mastery /></Protected>} />
            <Route path="/parent/curriculum-overview" element={<Protected role="Parent"><CurriculumOverview /></Protected>} />
            <Route path="/parent/library" element={<Protected role="Parent"><LessonLibrary /></Protected>} />
            <Route path="/parent/scripture" element={<Protected role="Parent"><ScripturePage /></Protected>} />
            <Route path="/parent/calendar" element={<Protected role="Parent"><CalendarPage /></Protected>} />
            <Route path="/parent/reading-plan" element={<Protected role="Parent"><ReadingPlanPage /></Protected>} />
            <Route path="/parent/badges" element={<Protected role="Parent"><BadgesPage /></Protected>} />
            <Route path="/parent/chores" element={<Protected role="Parent"><ChoresPage /></Protected>} />
            <Route path="/parent/screen-time" element={<Protected role="Parent"><ScreenTimePage /></Protected>} />
            <Route path="/parent/leaderboard" element={<Protected role="Parent"><LeaderboardPage /></Protected>} />
            <Route path="/parent/spelling-words" element={<Protected role="Parent"><SpellingBankPage /></Protected>} />
            <Route path="/parent/manual" element={<Protected role="Parent"><StudentManual /></Protected>} />
            <Route path="/parent/staff-manual" element={<Protected role="Parent"><StaffManual /></Protected>} />
            <Route path="/parent/guide/spelling-week-1" element={<Protected role="Parent"><TeacherGuideSpelling /></Protected>} />
            <Route path="/parent/guide" element={<Protected role="Parent"><StudentGuide /></Protected>} />
            <Route path="/student" element={<Protected role="Student"><StudentDashboard /></Protected>} />
            <Route path="/student/scripture" element={<Protected role="Student"><ScripturePage /></Protected>} />
            <Route path="/student/calendar" element={<Protected role="Student"><CalendarPage /></Protected>} />
            <Route path="/student/reading-plan" element={<Protected role="Student"><ReadingPlanPage /></Protected>} />
            <Route path="/student/badges" element={<Protected role="Student"><BadgesPage /></Protected>} />
            <Route path="/student/chores" element={<Protected role="Student"><ChoresPage /></Protected>} />
            <Route path="/student/screen-time" element={<Protected role="Student"><ScreenTimePage /></Protected>} />
            <Route path="/student/games" element={<Protected role="Student"><GamesPage /></Protected>} />
            <Route path="/student/leaderboard" element={<Protected role="Student"><LeaderboardPage /></Protected>} />
            <Route path="/student/manual" element={<Protected role="Student"><StudentManual /></Protected>} />
            <Route path="/student/guide" element={<Protected role="Student"><StudentGuide /></Protected>} />
            <Route path="/student/lesson/:id" element={<Protected role="Student"><LessonPage /></Protected>} />
            <Route path="/student/study-guide/:id" element={<Protected role="Student"><StudyGuide /></Protected>} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
